import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuildingCircleXmark,
    faLocationSlash,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
    DecisionEnum,
    type UnmatchedDataPointsListRequest,
} from "../../apiClient/generated";
import { useAppSelector, useUnmatchedDataApiClient } from "../../hooks";
import { ROUTES } from "../../routes";
import {
    BulkActionsMenu,
    type BulkActionsProps,
    BulkFailuresDownloadButton,
    BulkFailuresPreview,
    BulkMenuItem,
} from "../../ui/BulkTableAction";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { Modal } from "../../ui/Modals";
import { humanizeDecision } from "./decisions";

// Filters come from unmatchedDataPointTableFiltersAtom and are spread into the
// bulk endpoint, so they carry the data point list-request shape.
type DataPointBulkProps = BulkActionsProps<UnmatchedDataPointsListRequest>;

const dataPointFailureLink = (id: string) =>
    `${ROUTES.DASHBOARD_UNMATCHED_DATA}?dataPoint=${id}`;

export interface BulkDecisionModalProps extends DataPointBulkProps {
    decision: DecisionEnum;
    onClose: () => void;
}

const BulkDecisionModal = (props: BulkDecisionModalProps) => {
    const queryClient = useQueryClient();
    const apiClient = useUnmatchedDataApiClient();

    const decisionLabel = humanizeDecision(props.decision);

    const allSelectedCountQuery = useQuery({
        queryKey: ["UnmatchedDataPoint", props.filters, "count"],
        queryFn: async () => {
            return apiClient.unmatchedDataPointsList({
                ...props.filters,
                pageSize: 1,
            });
        },
        enabled: props.allSelected,
    });

    const [applyToAllFiltered, setApplyToAllFiltered] = useState(
        props.allSelected,
    );

    const affectedCount = applyToAllFiltered
        ? allSelectedCountQuery.data?.count
        : props.selectedIds.length;

    const [errorMessage, setErrorMessage] = useState<string>(null);

    const bulkActionMutation = useMutation({
        mutationFn: async () => {
            return apiClient.unmatchedDataPointsBulkSaveDecisionCreate({
                bulkUpdateUnmatchedDataPointsRequest: {
                    decision: props.decision,
                },
                ...(applyToAllFiltered
                    ? props.filters
                    : { ids: props.selectedIds }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries();
            props.onFinalizeAction();
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                setErrorMessage(
                    body?.error ?? "Something went wrong. Please try again.",
                );
            } catch {
                setErrorMessage("Something went wrong. Please try again.");
            }
        },
    });

    // Either the number of affected data points, or a spinner while the count
    // for "all filtered" is still loading.
    const affectedCountDisplay = affectedCount ?? (
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    );

    return (
        <Modal
            visible={true}
            onClose={bulkActionMutation.isPending ? undefined : props.onClose}
            size="sm"
        >
            <h2 className="text-lg font-semibold mb-4 mt-2">
                Mark as "{decisionLabel}"
            </h2>
            {bulkActionMutation.isIdle && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Are you sure you want to mark {affectedCountDisplay}{" "}
                        data point
                        {affectedCount !== 1 ? "s" : ""} as "{decisionLabel}"?
                    </p>
                    {props.allSelected && (
                        <div className="flex items-center gap-2 text-sm mt-4">
                            <CustomCheckbox
                                checked={applyToAllFiltered ? "yes" : "no"}
                                onClick={() =>
                                    setApplyToAllFiltered(!applyToAllFiltered)
                                }
                            />
                            Apply action to all filtered data points
                        </div>
                    )}
                    <div className="flex gap-2 mt-5">
                        <PrimaryButton
                            type="button"
                            disabled={
                                bulkActionMutation.isPending ||
                                (allSelectedCountQuery.isLoading &&
                                    props.allSelected)
                            }
                            onClick={() => bulkActionMutation.mutateAsync()}
                        >
                            Mark {affectedCountDisplay} data point
                            {affectedCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            disabled={bulkActionMutation.isPending}
                            onClick={props.onClose}
                        >
                            Cancel
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isPending && (
                <div className="w-full h-32 flex items-center justify-center gap-3 text-gray-500">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin h-6"
                    />
                    Saving decision on data points...
                </div>
            )}
            {bulkActionMutation.isSuccess && (
                <>
                    <p className="text-sm mb-1">
                        Successfully marked {bulkActionMutation.data.ok} data
                        point
                        {bulkActionMutation.data.ok !== 1 ? "s" : ""} as "
                        {decisionLabel}".
                    </p>
                    {bulkActionMutation.data.failed > 0 && (
                        <>
                            <p className="text-sm text-gray-600 mb-1">
                                {bulkActionMutation.data.failed} data point
                                {bulkActionMutation.data.failed !== 1
                                    ? "s"
                                    : ""}{" "}
                                could not be updated. Download the CSV to see
                                the reason for each failure.
                            </p>
                            <BulkFailuresPreview
                                linkForId={dataPointFailureLink}
                                result={bulkActionMutation.data}
                            />
                        </>
                    )}
                    <div className="flex gap-2 mt-4">
                        <BulkFailuresDownloadButton
                            linkForId={dataPointFailureLink}
                            result={bulkActionMutation.data}
                            filenamePrefix="bulk-decision"
                        />
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
            {bulkActionMutation.isError && (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-bold">Error:</span>{" "}
                        {errorMessage ??
                            "Something went wrong. Please try again."}
                    </p>
                    <div className="flex gap-2 mt-4">
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

export const BulkActions = (props: DataPointBulkProps) => {
    const canEdit = useAppSelector(
        (state) => state.auth.permissions.unmatchedEmissionEdit,
    );
    const [showBulkModal, setShowBulkModal] = useState<DecisionEnum | null>(
        null,
    );

    const bulkActionsDisabled = props.selectedIds.length === 0 || !canEdit;
    const noPermissionTitle =
        "You don't have permission to take decisions on unmatched data points.";

    return (
        <>
            <BulkActionsMenu disabled={bulkActionsDisabled}>
                <BulkMenuItem
                    icon={faBuildingCircleXmark}
                    label={`Mark as '${humanizeDecision(
                        DecisionEnum.NotOwnedByCompany,
                    )}'`}
                    enabled={canEdit}
                    disabledTitle={noPermissionTitle}
                    onClick={() =>
                        setShowBulkModal(DecisionEnum.NotOwnedByCompany)
                    }
                />
                <BulkMenuItem
                    icon={faLocationSlash}
                    label={`Mark as '${humanizeDecision(
                        DecisionEnum.OwnedButNoSite,
                    )}'`}
                    enabled={canEdit}
                    disabledTitle={noPermissionTitle}
                    onClick={() =>
                        setShowBulkModal(DecisionEnum.OwnedButNoSite)
                    }
                />
            </BulkActionsMenu>
            {showBulkModal !== null && (
                <BulkDecisionModal
                    {...props}
                    decision={showBulkModal}
                    onClose={() => setShowBulkModal(null)}
                />
            )}
        </>
    );
};
